import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { X } from "lucide-react";

export const CONSENT_KEY = "uknitwear_cookie_consent";

export const readConsent = () => {
    try {
        const raw = localStorage.getItem(CONSENT_KEY);
        return raw ? JSON.parse(raw) : null;
    } catch {
        return null;
    }
};

export const writeConsent = (prefs) => {
    const payload = {
        ...prefs,
        ts: new Date().toISOString(),
    };
    localStorage.setItem(CONSENT_KEY, JSON.stringify(payload));
    window.dispatchEvent(new CustomEvent("uknitwear:consent", { detail: payload }));
    return payload;
};

const CookieBanner = () => {
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        // Small delay so banner animates in after the page renders
        const t = setTimeout(() => {
            if (!readConsent()) setVisible(true);
        }, 400);
        return () => clearTimeout(t);
    }, []);

    const decide = (prefs) => {
        writeConsent(prefs);
        setVisible(false);
    };

    const acceptAll = () =>
        decide({ functional: true, analytics: true, marketing: true });

    const rejectOptional = () =>
        decide({ functional: false, analytics: false, marketing: false });

    if (!visible) return null;

    return (
        <div
            className="fixed inset-x-0 bottom-0 z-50 px-3 md:px-6 pb-3 md:pb-6 pointer-events-none"
            data-testid="cookie-banner"
            role="dialog"
            aria-live="polite"
            aria-label="Cookie consent"
        >
            <div className="pointer-events-auto relative max-w-5xl mx-auto bg-background border-4 border-ink shadow-[8px_8px_0_0_#E32636]">
                <button
                    type="button"
                    onClick={rejectOptional}
                    className="absolute top-2 right-2 p-1.5 hover:bg-ink hover:text-black"
                    aria-label="Dismiss and reject optional cookies"
                    data-testid="cookie-banner-dismiss"
                >
                    <X className="w-4 h-4" />
                </button>

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-0">
                    <div className="lg:col-span-7 p-5 md:p-7 lg:border-r-4 border-ink">
                        <div className="flex items-center gap-2">
                            <span className="w-2.5 h-2.5 bg-hazard" />
                            <span className="font-mono-ui text-[10px] uppercase tracking-[0.3em] text-hazard">
                                Cookies / Heads Up
                            </span>
                        </div>
                        <h3 className="mt-3 font-heading text-3xl md:text-4xl uppercase leading-[0.9]">
                            We Use <span className="text-hazard">Cookies.</span>
                        </h3>
                        <p className="mt-3 text-sm leading-relaxed text-foreground/80 max-w-xl">
                            Essential ones keep the cart and checkout running.
                            Optional ones (analytics, marketing) help us improve
                            the site and reach the right people. You decide.
                        </p>
                        <Link
                            to="/cookies"
                            onClick={() => setVisible(false)}
                            className="inline-block mt-3 font-mono-ui text-[10px] uppercase tracking-[0.25em] underline underline-offset-4 hover:text-hazard"
                            data-testid="cookie-banner-manage-link"
                        >
                            Manage Preferences →
                        </Link>
                    </div>

                    <div className="lg:col-span-5 p-5 md:p-7 bg-bone flex flex-col justify-center gap-2">
                        <button
                            type="button"
                            onClick={acceptAll}
                            className="w-full bg-ink text-black border-2 border-ink py-3 font-mono-ui text-xs uppercase tracking-[0.3em] hover:bg-hazard hover:border-hazard transition-colors"
                            data-testid="cookie-banner-accept-button"
                        >
                            Accept All
                        </button>
                        <button
                            type="button"
                            onClick={rejectOptional}
                            className="w-full bg-background border-2 border-ink py-3 font-mono-ui text-xs uppercase tracking-[0.3em] hover:bg-ink hover:text-black transition-colors"
                            data-testid="cookie-banner-reject-button"
                        >
                            Reject Optional
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CookieBanner;
